import {
  Children,
  cloneElement,
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';

import type { Options, Placement } from '@popperjs/core';
import { motion } from 'framer-motion';
import styled, { createGlobalStyle } from 'styled-components';
import create from 'zustand';

import { useAriaId, usePreviousValue } from '@components/component-helpers';
import { theme } from '@styles/theme';
import { debounce } from '@utils/browser';

export interface TooltipProps {
  header?: string;
  content?: ReactElement | string;
  children: ReactElement;
  placement?: Placement;
}

export const Tooltip = (props: TooltipProps) => {
  const { header, content, children } = props;
  if (!header && !content) {
    return children;
  }
  return <TooltipHandler {...props} />;
};

const TooltipHandler = ({ children, header, content, placement = 'bottom' }: TooltipProps) => {
  const {
    //
    getArrowProps,
    getTooltipProps,
    getTriggerProps,
    setTooltipRef,
    visible,
  } = usePopperTooltip({
    placement,
  });
  const fading = useGlobalTooltipState((state) => state.fading);
  const wasFading = usePreviousValue(fading);

  const trigger = Children.only(children);

  if (!trigger) return null;

  return (
    <>
      {cloneElement(trigger, { ...trigger.props, ...getTriggerProps() })}

      {visible &&
        createPortal(
          <div ref={setTooltipRef} {...getTooltipProps()}>
            <motion.div
              className="tooltip-box"
              initial={{ opacity: wasFading ? 1 : 0 }}
              animate={{ opacity: 1, transition: { duration: 0.2 } }}
            >
              {header && <TooltipHeader>{header}</TooltipHeader>}
              {content && <TooltipContent>{content}</TooltipContent>}
              <motion.div {...getArrowProps()} />
            </motion.div>
          </div>,
          document.body
        )}
    </>
  );
};

const TooltipHeader = styled.div({
  font: theme.font.bold14,
  whiteSpace: 'nowrap',
  height: '1.25rem',
});

const TooltipContent = styled.div({
  font: theme.font.semibold14,
  whiteSpace: 'pre-wrap',
  overflowWrap: 'break-word',
});

function usePopperTooltip(props: Partial<Options>) {
  const [triggerRef, setTriggerRef] = useState<HTMLElement | null>(null);
  const [tooltipRef, setTooltipRef] = useState<HTMLElement | null>(null);
  const id = useAriaId('tooltip');

  const { fading, hide, showTooltip: setShowTooltip, showingId } = useGlobalTooltipState();

  const visible = showingId === id;

  const timer = useRef<number>();

  useEffect(() => () => clearTimeout(timer.current), []);

  const { styles, attributes } = usePopper(triggerRef, tooltipRef, {
    modifiers: [{ name: 'offset', options: { offset: [0, 10] } }],
    placement: props.placement || 'top',
  });

  const hideTooltip = useCallback(() => {
    clearTimeout(timer.current);
    hide();
  }, [hide]);

  const showTooltip = useCallback(() => {
    clearTimeout(timer.current);
    if (fading) {
      setShowTooltip(id);
    } else {
      timer.current = window.setTimeout(() => {
        setShowTooltip(id);
      }, 600);
    }
  }, [fading, id, setShowTooltip]);

  useEffect(() => {
    if (triggerRef == null) return;

    triggerRef.addEventListener('focus', showTooltip);
    triggerRef.addEventListener('pointerenter', showTooltip);

    triggerRef.addEventListener('blur', hideTooltip);
    triggerRef.addEventListener('pointerleave', hideTooltip);

    return () => {
      triggerRef.removeEventListener('focus', showTooltip);
      triggerRef.removeEventListener('pointerenter', showTooltip);

      triggerRef.removeEventListener('blur', hideTooltip);
      triggerRef.removeEventListener('pointerleave', hideTooltip);
    };
  }, [triggerRef, showTooltip, hideTooltip]);

  const getTooltipProps = () => ({
    role: 'tooltip',
    id: id,
    className: 'tooltip-container',
    style: styles.popper,
    ...attributes.popper,
  });

  const getArrowProps = () => ({
    ...attributes.arrow,
    className: 'tooltip-arrow',
    style: styles.arrow,
    'data-popper-arrow': true,
  });

  const getTriggerProps = () => ({
    'aria-describedby': id,
    ref: setTriggerRef,
  });

  return {
    getArrowProps,
    getTooltipProps,
    getTriggerProps,
    setTooltipRef,
    visible,
  };
}

interface GlobalTooltipState {
  showingId: string | null;
  fading: boolean;
  fade(): void;
  showTooltip(id: string): void;
  hide(): void;
}

const useGlobalTooltipState = create<GlobalTooltipState>((set, get) => ({
  showingId: null,
  fading: false,
  showTooltip: (showingId) => set({ showingId, fading: true }),
  fade: debounce(() => {
    set({ fading: false });
  }, 1000),
  hide: () => {
    set({ showingId: null, fading: true });
    get().fade();
  },
}));

export const TooltipStyles = createGlobalStyle`
  .tooltip-container {
    --tooltip-bg: ${theme.color.indigo100};
    --tooltip-border: ${theme.color.indigo100};
    --tooltip-color: ${theme.color.white100};
    position: relative;
    z-index: 999999;
  }

  .tooltip-box {
    position: relative;
    background-color: var(--tooltip-bg);
    border-radius: 0.25rem;
    border: 0.0625rem solid var(--tooltip-border);
    box-shadow: ${theme.shadow.medium};
    font: ${theme.font.regular14};
    color: var(--tooltip-color);
    display: flex;
    flex-direction: column;
    text-align: center;
    padding: 1rem;
    max-width: 20rem;
    flex-shrink: 1;
  }

  .tooltip-arrow {
    height: 1rem;
    position: absolute;
    width: 1rem;
    pointer-events: none;
  }

  .tooltip-arrow::before {
    border-style: solid;
    content: '';
    display: block;
    height: 0;
    margin: auto;
    width: 0;
  }

  .tooltip-arrow::after {
    border-style: solid;
    content: '';
    display: block;
    height: 0;
    margin: auto;
    position: absolute;
    width: 0;
  }

  .tooltip-container[data-popper-placement*='bottom'] .tooltip-arrow {
    margin-top: -0.5rem;
    top: 0;
  }

  .tooltip-container[data-popper-placement*='bottom'] .tooltip-arrow::before {
    border-color: transparent transparent var(--tooltip-border) transparent;
    border-width: 0rem 0.5rem 0.5rem 0.5rem;
    position: absolute;
  }

  .tooltip-container[data-popper-placement*='bottom'] .tooltip-arrow::after {
    border-color: transparent transparent var(--tooltip-bg) transparent;
    border-width: 0 0.5rem 0.5rem 0.5rem;
  }

  .tooltip-container[data-popper-placement*='top'] .tooltip-arrow {
    bottom: 0;
    left: 0;
    margin-bottom: -1rem;
  }

  .tooltip-container[data-popper-placement*='top'] .tooltip-arrow::before {
    border-color: var(--tooltip-border) transparent transparent transparent;
    border-width: 0.5rem 0.5rem 0 0.5rem;
    position: absolute;
  }

  .tooltip-container[data-popper-placement*='top'] .tooltip-arrow::after {
    border-color: var(--tooltip-bg) transparent transparent transparent;
    border-width: 0.5rem 0.5rem 0 0.5rem;
  }

  .tooltip-container[data-popper-placement*='right'] .tooltip-arrow {
    left: 0;
  }

  .tooltip-container[data-popper-placement*='right'] .tooltip-arrow::before {
    border-color: transparent var(--tooltip-border) transparent transparent;
    border-width: 0.5rem 0.5rem 0.5rem 0;
  }

  .tooltip-container[data-popper-placement*='right'] .tooltip-arrow::after {
    border-color: transparent var(--tooltip-bg) transparent transparent;
    border-width: 0.5rem 0.5rem 0.5rem 0;
    left: -0.5rem;
    top: 0;
  }

  .tooltip-container[data-popper-placement*='left'] .tooltip-arrow {
    right: 0;
  }

  .tooltip-container[data-popper-placement*='left'] .tooltip-arrow::before {
    border-color: transparent transparent transparent var(--tooltip-border);
    border-width: 0.5rem 0 0.5rem 0.5rem;
  }

  .tooltip-container[data-popper-placement*='left'] .tooltip-arrow::after {
    border-color: transparent transparent transparent var(--tooltip-bg);
    border-width: 0.5rem 0 0.5rem 0.5rem;
    left: 1rem;
    top: 0;
  }
`;
